import {
  base64DecodedByteLength,
  FEEDBACK_CATEGORIES,
  FEEDBACK_DETAILS_MAX_BYTES,
  FEEDBACK_DIAGNOSTICS_MAX_BYTES,
  FEEDBACK_GENERIC_FAILURE_MESSAGE,
  FEEDBACK_SCREENSHOT_MAX_BYTES,
  FEEDBACK_SCREENSHOT_MAX_COUNT,
  FEEDBACK_SUMMARY_MAX_LENGTH,
  type FeedbackCategory,
  type FeedbackDraft,
  type FeedbackFailureReason,
  type FeedbackFieldErrors,
  type FeedbackSubmission,
} from "@afar-research/contracts";
import { ConnectError } from "@afar-research/shared/connect";

/** The part of a submission the submitter wrote, once it is known Connect will admit it. */
export type ValidFeedbackDraft = Pick<
  FeedbackSubmission,
  "category" | "summary" | "details" | "screenshots" | "origin" | "diagnostics"
>;

export function validateFeedbackDraft(
  input: FeedbackDraft,
):
  | { readonly valid: true; readonly value: ValidFeedbackDraft }
  | { readonly valid: false; readonly errors: FeedbackFieldErrors } {
  const category = input.category.trim();
  const summary = input.summary.trim();
  const details = input.details.trim();
  const errors: { -readonly [K in keyof FeedbackFieldErrors]: string } = {};

  if (!FEEDBACK_CATEGORIES.some((candidate) => candidate === category)) {
    errors.category = "Choose a category.";
  }
  if (summary.length === 0) {
    errors.summary = "Enter a summary.";
  } else if (summary.length > FEEDBACK_SUMMARY_MAX_LENGTH) {
    errors.summary = `Keep the summary under ${FEEDBACK_SUMMARY_MAX_LENGTH} characters.`;
  }
  if (details.length === 0) {
    errors.details = "Enter details.";
  } else if (new TextEncoder().encode(details).byteLength > FEEDBACK_DETAILS_MAX_BYTES) {
    errors.details = "These details are too long to send. Shorten them and try again.";
  }
  if (
    input.diagnostics &&
    new TextEncoder().encode(input.diagnostics.text).byteLength > FEEDBACK_DIAGNOSTICS_MAX_BYTES
  ) {
    errors.diagnostics =
      "The diagnostics location is too long to send. Untick it, or shorten your Environment names.";
  }
  const screenshots = input.screenshots ?? [];
  if (screenshots.length > FEEDBACK_SCREENSHOT_MAX_COUNT) {
    errors.screenshots = `Attach up to ${FEEDBACK_SCREENSHOT_MAX_COUNT} screenshots.`;
  } else if (
    screenshots.some(
      (screenshot) => base64DecodedByteLength(screenshot.data) > FEEDBACK_SCREENSHOT_MAX_BYTES,
    )
  ) {
    errors.screenshots = "A screenshot is too large to send. Remove it and try again.";
  }

  if (Object.keys(errors).length > 0) {
    return { valid: false, errors };
  }

  return {
    valid: true,
    value: {
      category: category as FeedbackCategory,
      summary: input.summary,
      details: input.details,
      ...(screenshots.length > 0 ? { screenshots } : {}),
      ...(input.origin ? { origin: input.origin } : {}),
      ...(input.diagnostics ? { diagnostics: input.diagnostics } : {}),
    },
  };
}

/**
 * Connect checks each screenshot's bytes against its declared type, which the client cannot know
 * before sending; its refusal is answered as the field error it is.
 */
export function screenshotRefusalFieldErrors(error: unknown): FeedbackFieldErrors | null {
  return error instanceof ConnectError && error.code === "invalid_screenshot"
    ? {
        screenshots: "One of the screenshots is not an image we can send. Remove it and try again.",
      }
    : null;
}

export interface FeedbackFailure {
  readonly message: string;
  readonly reason: FeedbackFailureReason;
}

/**
 * What to tell a submitter whose report was refused.
 *
 * Every refusal ADR-0105 designed — a per-account quota, an operator block, the payload caps, the
 * stage breaker — used to arrive here as a bare transport status and leave as one of two
 * sentences, so the app asked people to sign in to accounts they were already signed in to and
 * blamed the network for decisions Connect had made deliberately (#1008). The code Connect
 * sent is the answer; the status is only a fallback for a refusal that carried no code.
 *
 * `reason` is separate from the sentence because one caller has to act rather than read: the
 * Feedback panel re-reads its account status when the credential is the problem, so it stops
 * presenting itself as signed in.
 */
export function describeFeedbackFailure(error: unknown): FeedbackFailure {
  if (error instanceof ConnectError) {
    switch (error.code) {
      case "feedback_quota":
        return {
          reason: "rejected",
          message: "You have sent several reports recently. Try again later.",
        };
      case "feedback_blocked":
        return {
          reason: "rejected",
          message:
            "This account cannot send feedback. Email us instead if you think that is a mistake.",
        };
      case "feedback_too_large":
        return {
          reason: "rejected",
          message:
            "This report is too large to send. Remove a screenshot or shorten the details, then try again.",
        };
      case "feedback_in_progress":
        return {
          reason: "unavailable",
          message:
            "This report is still being sent. Wait a couple of minutes, then send it again to confirm it arrived.",
        };
      case "feedback_unavailable":
        return {
          reason: "unavailable",
          message: "Feedback is temporarily unavailable. Try again later.",
        };
      case "identity_unavailable":
        return {
          reason: "unavailable",
          message:
            "We could not check your account just now. Your sign-in is fine — try again in a moment.",
        };
      default:
        break;
    }
    // No recognized code. Only a 401 is about who is asking; a 403 is about what they are
    // allowed to do, and telling someone to sign in over one sends them to fix the wrong thing.
    if (error.status === 401) {
      return {
        reason: "sign-in-required",
        message: "Sign in to your AFAR account again before sending feedback.",
      };
    }
    if (error.status === 403) {
      return {
        reason: "rejected",
        message: "This account is not allowed to send feedback from this build.",
      };
    }
  }
  return { reason: "unavailable", message: FEEDBACK_GENERIC_FAILURE_MESSAGE };
}
